// TelcoTrack — BUDOVA map marker model (square + horizontal milestone stripes).

import type { BudovaBuildingType } from './budova-building-type.constants';
import {
  type BudovaMilestoneEngineContext,
  type BudovaMilestoneId,
  BUDOVA_REQUIRED_MILESTONES,
  allMilestonesComplete,
  isMilestoneComplete,
} from './budova-milestone-engine';
import { PHASE_COLORS, PHASE_CODE } from './phase-codes';
import { isSamePhase } from './workflow-model';

/** Nevybraný typ budovy — plný červený čtverec. */
export const BUDOVA_MAP_MARKER_UNKNOWN_COLOR = PHASE_COLORS.RED;

/** Nerealizované (ZAMITNUTA) — plný černý čtverec. */
export const BUDOVA_MAP_MARKER_ZAMITNUTA_COLOR = PHASE_COLORS.BLACK;

/** Map marker canvas (web SVG viewBox / Android bitmap). */
export const BUDOVA_MAP_MARKER_VIEWBOX_SIZE = 36;
export const BUDOVA_MAP_MARKER_SQUARE_INSET = 2;
/** Visible gap between horizontal milestone stripes (viewBox px). */
export const BUDOVA_MAP_MARKER_STRIPE_GAP_PX = 2;

export interface BudovaMapMarkerStripeRect {
  y: number;
  height: number;
}

/** Stripe band positions inside the square (top → bottom), with fixed gaps between bands. */
export function computeBudovaMapStripeRects(stripeCount: number): readonly BudovaMapMarkerStripeRect[] {
  if (stripeCount <= 0) return [];
  const squareSize = BUDOVA_MAP_MARKER_VIEWBOX_SIZE - BUDOVA_MAP_MARKER_SQUARE_INSET * 2;
  const gap = BUDOVA_MAP_MARKER_STRIPE_GAP_PX;
  const gapsTotal = gap * (stripeCount - 1);
  const stripeHeight = (squareSize - gapsTotal) / stripeCount;
  return Array.from({ length: stripeCount }, (_, index) => ({
    y: BUDOVA_MAP_MARKER_SQUARE_INSET + index * (stripeHeight + gap),
    height: stripeHeight,
  }));
}

/** PRIPOJENA stripes top → bottom (same order as popup milestone buttons). */
export const BUDOVA_PRIPOJENA_MAP_STRIPE_MILESTONES: readonly BudovaMilestoneId[] =
  BUDOVA_REQUIRED_MILESTONES.PRIPOJENA;

/**
 * PRIPOJENA cascade order (inner→outer on legacy ring markers): Odbočené → Zřízené VR → Zavrtaný.
 * Colors are computed in this order, then mapped onto [BUDOVA_PRIPOJENA_MAP_STRIPE_MILESTONES].
 */
export const BUDOVA_PRIPOJENA_MAP_CASCADE_MILESTONES: readonly BudovaMilestoneId[] = [
  'ODBOCENO',
  'ZRIZENE_VR',
  'ZAVRTANY',
];

/** Bábovka stripes top → bottom (same order as popup milestone buttons). */
export const BUDOVA_BABOVKA_MAP_STRIPE_MILESTONES: readonly BudovaMilestoneId[] =
  BUDOVA_REQUIRED_MILESTONES.BABOVKA;

/** Bábovka cascade matches popup order (Odbočené → Připravená bábovka). */
export const BUDOVA_BABOVKA_MAP_CASCADE_MILESTONES: readonly BudovaMilestoneId[] =
  BUDOVA_BABOVKA_MAP_STRIPE_MILESTONES;

export type BudovaMapMarkerKind = 'unknown' | 'zamitnuta' | 'striped';

export interface BudovaMapMarkerStripe {
  milestoneId: BudovaMilestoneId;
  color: string;
  complete: boolean;
}

export interface BudovaMapMarkerModel {
  kind: BudovaMapMarkerKind;
  solidColor: string;
  stripes: readonly BudovaMapMarkerStripe[];
}

/** Stable cache key for Android/web bitmap/icon registration. */
export function buildBudovaMapMarkerIconId(model: BudovaMapMarkerModel): string {
  if (model.kind !== 'striped' || !model.stripes.length) {
    return `budova-solid-${model.solidColor.replace('#', '')}`;
  }
  const parts = model.stripes.map((s) => s.color.replace('#', ''));
  return `budova-${parts.join('-')}`;
}

export function resolveBudovaWorkflowNepripojenyPhaseCode(
  status: string | null | undefined,
): string {
  const s = (status ?? '').trim();
  const dot = s.indexOf('.PHASE.');
  if (dot > 0) return `${s.slice(0, dot)}.PHASE.0`;
  return 'BUDOVA.2.PHASE.0';
}

export function shouldUseBudovaAggregateMapStripeColors(
  buildingType: BudovaBuildingType,
  status: string | null | undefined,
  ctx: BudovaMilestoneEngineContext,
): boolean {
  if (isSamePhase(status, PHASE_CODE.BUDOVA_NEPRIPOJENY)) return false;
  // Post-Nepřipojený workflow status implies milestones were satisfied when advancing.
  // Map cache may omit branch VYKOP / junction objects, so Odbočené branch rules cannot
  // be re-evaluated here — still show aggregate phase color on PHASE.1+.
  if (buildingType === 'PRIPOJENA' || buildingType === 'BABOVKA') return true;
  return allMilestonesComplete(buildingType, ctx);
}

/**
 * Stripe color for one milestone on Nepřipojený (PHASE.0):
 * complete → aggregate next phase (Připojené plasty / Připravená bábovka), incomplete → initial phase.
 */
/** Visible complete-stripe color — distinct from Nepřipojený even when drawing maps PHASE.0/1 to same hex (e.g. optika). */
export function resolveBudovaMapMarkerNextPhaseColor(
  initialPhaseColor: string,
  nextPhaseColor: string,
): string {
  const initial = initialPhaseColor.trim().toLowerCase();
  const next = nextPhaseColor.trim().toLowerCase();
  if (next && next !== initial) return nextPhaseColor;
  return PHASE_COLORS.ORANGE;
}

export function resolveBudovaMilestoneStripeColor(
  complete: boolean,
  initialPhaseColor: string,
  nextPhaseColor: string,
): string {
  if (!complete) return initialPhaseColor;
  return resolveBudovaMapMarkerNextPhaseColor(initialPhaseColor, nextPhaseColor);
}

/** Map stripe colors onto visual order (popup top→bottom). */
export function resolveBudovaMapStripeColors(
  visualOrder: readonly BudovaMilestoneId[],
  _cascadeOrder: readonly BudovaMilestoneId[],
  isComplete: (id: BudovaMilestoneId) => boolean,
  initialPhaseColor: string,
  nextPhaseColor: string,
): string[] {
  return visualOrder.map((milestoneId) =>
    resolveBudovaMilestoneStripeColor(isComplete(milestoneId), initialPhaseColor, nextPhaseColor),
  );
}

function cascadeOrderForBuildingType(buildingType: BudovaBuildingType): readonly BudovaMilestoneId[] {
  if (buildingType === 'PRIPOJENA') return BUDOVA_PRIPOJENA_MAP_CASCADE_MILESTONES;
  if (buildingType === 'BABOVKA') return BUDOVA_BABOVKA_MAP_CASCADE_MILESTONES;
  return [];
}

function buildStripedModel(
  buildingType: BudovaBuildingType,
  stripeMilestones: readonly BudovaMilestoneId[],
  ctx: BudovaMilestoneEngineContext,
  options: {
    initialPhaseColor: string;
    /** Current workflow status color (aggregate when post-Nepřipojený). */
    aggregatePhaseColor: string;
    /** Color of aggregate PHASE.1 after all milestones (Připojené plasty / Připravená bábovka). */
    nextPhaseColor: string;
    status: string | null | undefined;
  },
): BudovaMapMarkerModel {
  const isComplete = (id: BudovaMilestoneId) =>
    isMilestoneComplete(buildingType, id, ctx);

  if (shouldUseBudovaAggregateMapStripeColors(buildingType, options.status, ctx)) {
    const color = options.aggregatePhaseColor;
    return {
      kind: 'striped',
      solidColor: color,
      stripes: stripeMilestones.map((milestoneId) => ({
        milestoneId,
        color,
        complete: isComplete(milestoneId),
      })),
    };
  }

  const colors = resolveBudovaMapStripeColors(
    stripeMilestones,
    cascadeOrderForBuildingType(buildingType),
    isComplete,
    options.initialPhaseColor,
    options.nextPhaseColor,
  );

  return {
    kind: 'striped',
    solidColor: options.aggregatePhaseColor,
    stripes: stripeMilestones.map((milestoneId, index) => ({
      milestoneId,
      color: colors[index] ?? options.initialPhaseColor,
      complete: isComplete(milestoneId),
    })),
  };
}

export function resolveBudovaMapMarkerModel(
  buildingType: BudovaBuildingType | null | undefined,
  ctx: BudovaMilestoneEngineContext,
  options: {
    initialPhaseColor: string;
    aggregatePhaseColor: string;
    nextPhaseColor: string;
    status: string | null | undefined;
  },
): BudovaMapMarkerModel {
  if (!buildingType) {
    return {
      kind: 'unknown',
      solidColor: BUDOVA_MAP_MARKER_UNKNOWN_COLOR,
      stripes: [],
    };
  }
  if (buildingType === 'ZAMITNUTA') {
    return {
      kind: 'zamitnuta',
      solidColor: BUDOVA_MAP_MARKER_ZAMITNUTA_COLOR,
      stripes: [],
    };
  }
  if (buildingType === 'PRIPOJENA') {
    return buildStripedModel(buildingType, BUDOVA_PRIPOJENA_MAP_STRIPE_MILESTONES, ctx, options);
  }
  if (buildingType === 'BABOVKA') {
    return buildStripedModel(buildingType, BUDOVA_BABOVKA_MAP_STRIPE_MILESTONES, ctx, options);
  }
  return {
    kind: 'unknown',
    solidColor: BUDOVA_MAP_MARKER_UNKNOWN_COLOR,
    stripes: [],
  };
}
